import { A, pipe } from "@mobily/ts-belt"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { CourseImport } from "@shared/models/analytics/course"
import type { LessonCreate } from "@shared/models/analytics/lesson"
import type { UnitCreate } from "@shared/models/analytics/unit"
import type { Course, CourseCalendarDate, Unit } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type { TransferrableDate } from "@shared/models/transferrable-date"
import Patch from "@shared/models/patch-updater"
import {
  consolidateCourseDatesOff,
  constructUnitsAndDates,
  insertRange,
} from "cc-frontend/lib/unit-utils"
import type { PreparedAction } from "../../../action-doc-builder"
import { isCourseDateCustom } from "cc-frontend/utils/filter-dates/course"
import { compact, filter, find, partial } from "lodash-es"
import moment from "moment"
import type { Narrative } from "../../create-narrative"

interface Payload {
  transferrableArray: Array<TransferrableDate>
  newStartDate: string
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  originalCourseId: string | null
  originalPlanbookId: string | null
}

export default function importTransferrableArrayAction(payload: Payload): PreparedAction {
  let schoolDays = payload.rotationCalendar.attributes.schoolDays

  let consolidatedCourseDatesOff = consolidateCourseDatesOff(
    payload.course,
    payload.planbook,
    payload.rotationCalendar
  )

  let newCourseDates = insertRange(
    payload.transferrableArray,
    payload.newStartDate,
    1,
    null,
    payload.course.attributes.calendar.dates,
    consolidatedCourseDatesOff,
    schoolDays,
    true
  )

  let newDatesUnits: { courseDates: Array<CourseCalendarDate>; units: Array<Unit> } | null = null
  try {
    newDatesUnits = constructUnitsAndDates(newCourseDates, consolidatedCourseDatesOff, schoolDays)
  } catch (e) {
    console.error(e)
  }

  return {
    name: "IMPORT_TRANSFERRABLE_ARRAY",
    payload: payload,
    patches:
      newDatesUnits === null
        ? []
        : patches(payload.course.id, newDatesUnits.courseDates, newDatesUnits.units),
    undoPatches: undoPatches(payload),
    narrative:
      newDatesUnits === null
        ? null
        : partial(narrative, payload, newDatesUnits.courseDates, newDatesUnits.units),
  }
}

function patches(courseId: string, courseDates: Array<CourseCalendarDate>, units: Array<Unit>) {
  let patch = Patch.create("course", courseId)
    .inc(`attributes.calendar.version`, 1)
    .set(`attributes.calendar.dates`, courseDates)
    .set(`attributes.calendar.units`, units).patch

  return [patch]
}

function undoPatches(payload: Payload) {
  let patch = Patch.create("course", payload.course.id)
    .inc(`attributes.calendar.version`, 1)
    .set(
      "attributes.calendar.dates",
      JSON.parse(JSON.stringify(payload.course.attributes.calendar.dates))
    )
    .set(
      "attributes.calendar.units",
      JSON.parse(JSON.stringify(payload.course.attributes.calendar.units))
    ).patch
  return [patch]
}

export function narrative(
  payload: Payload,
  courseDates: Array<CourseCalendarDate>,
  units: Array<Unit>,
  findFn: FindFn,
  userId: string
): Narrative {
  let formattedDateString = moment(payload.newStartDate).format("ddd, MMM. D, YYYY")
  let originalPlanbookIdAndOriginalCourseId = compact([
    payload.originalPlanbookId,
    payload.originalCourseId,
  ])

  let unitCount = pipe(
    payload.transferrableArray,
    A.map((el) => el.unitStart),
    A.flat,
    A.uniq,
    A.length
  )

  // Overall event
  // course event
  let courseEvent: CourseImport = {
    ...createBlankEvent(),
    root_id: payload.planbook.id,
    root_type: "planbook",

    primary_verb: "edit",
    primary_possessive: "owns",

    primary_object_type: "course",
    primary_object_id: payload.course.id,
    primary_object_specifier: "import",
    primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],

    adverbial_verb: "importing",

    adverbial_object_type: "course",
    adverbial_object_id: payload.originalCourseId,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: originalPlanbookIdAndOriginalCourseId,
  }

  let courseDateEvents = courseDates.map((el): LessonCreate => {
    let dateScheduledFor = find(
      filter(courseDates, isCourseDateCustom),
      (date) => date.attributes.cardStackId === el.attributes.cardStackId!
    )
    return {
      ...createBlankEvent(),
      root_id: payload.planbook.id,
      root_type: "planbook",
      primary_object_id: payload.course.id,
      date_plan_scheduled_for: dateScheduledFor?.attributes.dateString || null,
      primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
      primary_verb: "create",
      primary_possessive: "owns",
      primary_object_type: "lesson",
      primary_object_specifier: null,
      adverbial_verb: "importing",
      adverbial_object_type: "lesson",
      adverbial_object_specifier: null,
      adverbial_object_id: el.attributes.cardStackId,
      adverbial_object_id_and_ancestor_ids: originalPlanbookIdAndOriginalCourseId,
    }
  })

  let unitEvents = units.map((el): UnitCreate => {
    return {
      ...createBlankEvent(),
      root_id: payload.planbook.id,
      root_type: "planbook",
      date_plan_scheduled_for: null,
      primary_verb: "create",
      primary_possessive: "owns",

      primary_object_type: "unit",
      primary_object_id: el.id,
      primary_object_specifier: "import",
      primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],

      adverbial_verb: "importing",

      adverbial_object_type: "unit",
      adverbial_object_id: el.id,
      adverbial_object_specifier: null,
      adverbial_object_id_and_ancestor_ids: originalPlanbookIdAndOriginalCourseId,
    }
  })

  return {
    title: `Imported ${payload.transferrableArray.length} lessons and ${unitCount} units`,
    titleForAnalytics: "Import Lessons Modal - Import Successful",
    description: `Imported onto ${formattedDateString} in "${payload.course.attributes.title}"`,
    context: {
      lessonCount: payload.transferrableArray.length,
      unitCount: unitCount,
      courseTitle: payload.course.attributes.title,
      planbookTitle: payload.planbook.attributes.title,
      courseId: payload.course.id,
      planbookId: payload.planbook.id,
    },
    activity: {
      actor: `user-${userId}`,
      verb: "edited",
      object: `course-${payload.course.id}`,
      to: [
        `course-edits:course-${payload.course.id}`,
        `planbook-edits:planbook-${payload.planbook.id}`,
      ],
    },
    newAnalyticsEvents: [courseEvent, ...courseDateEvents, ...unitEvents],
  }
}
